<template>
  <component
    :is="tagName"
    ref="rootEl"
    class="x-text"
    :class="[
      `x-text--${variant}`,
      weightClass,
      colorClass,
      hoverColorClass,
      { 'x-text--multi': isMultiLine },
    ]"
    :style="inlineStyle"
    v-bind="$attrs"
  >
    <slot />
  </component>
</template>

<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref } from 'vue';
import { elementObserverManager } from '../../libs/element-observer-manager';
import type { TextColor, Variant, Weight } from './types';

interface Props {
  variant?: Variant;
  as?: string;
  weight?: Weight;
  /** 文字颜色：层级颜色 (t1-t8)、功能颜色 (p6/p7/s6/s7/e6/e7/w6/w7/b6) 或自定义颜色值 */
  color?: TextColor;
  /** Hover 状态下的文字颜色 */
  hoverColor?: TextColor;
}

// 将常量移到组件外部，避免每个实例都创建
// 使用 Set<string> 类型，支持预设颜色和自定义颜色字符串
const PRESET_COLORS_SET = new Set<string>([
  't1',
  't3',
  't5',
  't6',
  't7',
  't8',
  'p6',
  'p7',
  's6',
  's7',
  'e6',
  'e7',
  'w6',
  'w7',
  'b6',
]);

// 颜色值到 CSS 变量的映射
const COLOR_VAR_MAP: Record<string, string> = {
  t1: 'var(--x-text-1)',
  t3: 'var(--x-text-3)',
  t5: 'var(--x-text-5)',
  t6: 'var(--x-text-6)',
  t7: 'var(--x-text-7)',
  t8: 'var(--x-text-8)',
  p6: 'var(--x-purple-6)',
  p7: 'var(--x-purple-7)',
  s6: 'var(--x-success-6)',
  s7: 'var(--x-success-7)',
  e6: 'var(--x-error-6)',
  e7: 'var(--x-error-7)',
  w6: 'var(--x-warning-6)',
  w7: 'var(--x-warning-7)',
  b6: 'var(--x-purple-6)',
};

// 常规色到 hover 色的自动转换映射
const COLOR_TO_HOVER_MAP: Record<string, string> = {
  // 品牌色：p6 -> p7, p7 -> p7
  p6: 'p7',
  p7: 'p7',
  // 成功色：s6 -> s7, s7 -> s6
  s6: 's7',
  s7: 's6',
  // 错误色：e6 -> e7, e7 -> e6
  e6: 'e7',
  e7: 'e6',
  // 警告色：w6 -> w7, w7 -> w6
  w6: 'w7',
  w7: 'w6',
  // 超链接：b6 -> p7
  b6: 'p7',
};

const VARIANT_MAP: Record<string, string> = {
  '10': 'span',
  '12': 'span', // 辅助文字
  '14': 'p', // 正文
  '16': 'h5', // 小标题
  '18': 'h4', // 中标题
  '20': 'h3', // 大标题
  '24': 'h2', // 超大标题
  '28': 'h1', // 超大标题
  '32': 'h3', // 超大标题
  '48': 'h2', // 超大标题
  '72': 'h1', // 超大标题
};

const props = withDefaults(defineProps<Props>(), {
  variant: '14',
  as: undefined,
  weight: 'regular',
  color: 't3', // 默认使用正文颜色
  hoverColor: undefined,
});

// 将变体映射到 HTML 标签
const tagName = computed(() => {
  if (props.as) return props.as;
  return VARIANT_MAP[props.variant] || 'p';
});

const rootEl = ref<HTMLElement | null>(null);
const isMultiLine = ref(false);

const weightClass = computed(() => {
  if (typeof props.weight === 'number') return '';
  return props.weight === 'medium' ? 'x-text--weight-medium' : 'x-text--weight-regular';
});

// 计算颜色类名（统一使用 x-text--color-* 格式）
const colorClass = computed(() => {
  if (!props.color) return '';

  // 使用 Set 查找，性能更好
  if (PRESET_COLORS_SET.has(props.color)) {
    return `x-text--color-${props.color}`;
  }
  // 否则返回空，使用内联样式
  return '';
});

// 计算 hover 颜色类名（统一使用一个类，通过 CSS 变量控制）
const hoverColorClass = computed(() => {
  // 计算实际的 hover 颜色
  let actualHoverColor: string | undefined = props.hoverColor;
  if (!actualHoverColor && props.color && PRESET_COLORS_SET.has(props.color)) {
    actualHoverColor = COLOR_TO_HOVER_MAP[props.color];
  }

  // 如果有 hover 颜色（预设或自定义），添加 hover 类
  if (actualHoverColor) {
    return 'x-text--has-hover';
  }

  return '';
});

// 计算内联样式 - 优化：避免不必要的对象创建
const inlineStyle = computed(() => {
  const hasColor = props.color && !PRESET_COLORS_SET.has(props.color);

  // 计算实际的 hover 颜色（优先使用 props.hoverColor，否则自动转换）
  let actualHoverColor: string | undefined = props.hoverColor;
  if (!actualHoverColor && props.color) {
    if (PRESET_COLORS_SET.has(props.color)) {
      // 预设颜色自动转换，通过 CSS 变量设置
      actualHoverColor = COLOR_TO_HOVER_MAP[props.color];
    } else {
      // 自定义颜色，如果没有指定 hoverColor，使用原色作为 hover 色
      actualHoverColor = props.color;
    }
  }

  // 如果没有自定义颜色且没有 hover 颜色，返回空对象
  if (!hasColor && !actualHoverColor) {
    return {};
  }

  const styles: Record<string, string> = {};

  // 普通颜色（只有自定义颜色才需要内联样式）
  if (hasColor) {
    styles.color = props.color;
  }

  // hover颜色：通过 CSS 变量统一控制
  if (actualHoverColor) {
    if (PRESET_COLORS_SET.has(actualHoverColor)) {
      // 预设颜色使用 CSS 变量
      styles['--x-text-hover-color'] = COLOR_VAR_MAP[actualHoverColor];
    } else {
      // 自定义颜色直接使用颜色值
      styles['--x-text-hover-color'] = actualHoverColor;
    }
  }

  return styles;
});

let idleCallbackId: number | null = null;
// 缓存样式计算结果，避免频繁调用 getComputedStyle
let cachedLineHeight: number | null = null;
let cachedFontSize: number | null = null;
let lastVariant: Variant | undefined = undefined;

function scheduleIdleUpdate(callback: () => void) {
  if (typeof window !== 'undefined' && typeof window.requestIdleCallback === 'function') {
    return window.requestIdleCallback(() => callback(), { timeout: 100 });
  }

  return window.setTimeout(callback, 16);
}

function cancelIdleUpdate(id: number) {
  if (typeof window !== 'undefined' && typeof window.cancelIdleCallback === 'function') {
    window.cancelIdleCallback(id);
    return;
  }

  window.clearTimeout(id);
}

function updateMulti() {
  if (!rootEl.value) return;

  const el = rootEl.value;
  const textContent = el.textContent?.trim() || '';

  if (!textContent) {
    isMultiLine.value = false;
    cachedLineHeight = null;
    cachedFontSize = null;
    return;
  }

  // 如果 variant 变化，清除缓存
  if (lastVariant !== props.variant) {
    cachedLineHeight = null;
    cachedFontSize = null;
    lastVariant = props.variant;
  }

  // 缓存 getComputedStyle 的结果，避免频繁调用
  let lineHeight = cachedLineHeight;
  let fontSize = cachedFontSize;

  if (lineHeight === null || fontSize === null) {
    const computedStyle = getComputedStyle(el);
    lineHeight = parseFloat(computedStyle.lineHeight);
    fontSize = parseFloat(computedStyle.fontSize);
    cachedLineHeight = lineHeight;
    cachedFontSize = fontSize;
  }

  // 如果 lineHeight 是 normal，使用 fontSize * 1.2 作为默认行高
  const actualLineHeight = isNaN(lineHeight) ? fontSize * 1.2 : lineHeight;

  // 如果元素高度明显超过单行高度，说明换行了
  // 使用 1.5 倍行高作为阈值，避免因为字体渲染差异导致的误判
  const newIsMultiLine = el.offsetHeight > actualLineHeight * 1.5;

  // 只在值变化时更新，避免不必要的响应式触发
  if (isMultiLine.value !== newIsMultiLine) {
    isMultiLine.value = newIsMultiLine;
  }
}

// 保存清理函数的引用
let cleanupResize: (() => void) | null = null;
let cleanupMutation: (() => void) | null = null;
let cleanupWindowResize: (() => void) | null = null;

onMounted(() => {
  if (!rootEl.value) return;

  const element = rootEl.value;

  // 优先使用 requestIdleCallback，Safari 回退到 setTimeout
  const tick = () => {
    if (idleCallbackId !== null) {
      cancelIdleUpdate(idleCallbackId);
    }
    idleCallbackId = scheduleIdleUpdate(updateMulti);
  };

  // 初次计算
  tick();

  // 使用观察者管理器注册观察者
  cleanupResize = elementObserverManager.observeResize(element, tick);
  cleanupMutation = elementObserverManager.observeMutation(element, tick);
  cleanupWindowResize = elementObserverManager.observeWindowResize(tick);
});

onUnmounted(() => {
  // 清理所有观察者
  if (cleanupResize) {
    cleanupResize();
    cleanupResize = null;
  }

  if (cleanupMutation) {
    cleanupMutation();
    cleanupMutation = null;
  }

  if (cleanupWindowResize) {
    cleanupWindowResize();
    cleanupWindowResize = null;
  }

  // 清理 requestIdleCallback
  if (idleCallbackId !== null) {
    cancelIdleUpdate(idleCallbackId);
    idleCallbackId = null;
  }

  // 清除缓存
  cachedLineHeight = null;
  cachedFontSize = null;
  lastVariant = undefined;
});
</script>

<style lang="scss" scoped>
.x-text {
  color: var(--x-text-1);
  /* 字体家族：优先使用系统默认字体 */
  font-family:
    -apple-system, BlinkMacSystemFont, 'Helvetica Neue', Helvetica, 'Segoe UI', Arial, Roboto,
    'PingFang SC', 'miui', 'Hiragino Sans GB', 'Microsoft YaHei', sans-serif;
}

.x-text[as='a']:hover {
  color: var(--x-text-1);
  text-decoration: underline;
}

/* 字号 - 与 Variant 对应 */
$font-variants: 10, 12, 14, 16, 18, 20, 24, 28, 32, 48, 72;

@each $size in $font-variants {
  .x-text--#{$size} {
    font-size: var(--x-text-#{$size}-size);
    line-height: var(--x-text-#{$size}-line-height);
  }

  .x-text--#{$size}.x-text--multi {
    line-height: var(--x-text-#{$size}-multi-line-height);
  }
}

/* 字重 */
.x-text--weight-regular {
  font-weight: var(--x-weight-regular);
}

.x-text--weight-medium {
  font-weight: var(--x-weight-medium);
}

/*
 * 文字颜色 - 统一使用 x-text--color-* 命名规范
 *
 * 层级颜色：
 * T1: 标题 - 强调层级、突出内容
 * T3: 正文 - 承载主要信息，长期阅读
 * T5: 次要信息 - 辅助说明、弱化层级
 * T6: 提示信息 - 弱化存在、必要时注意到
 * T7: 禁用1 - 提示信息
 * T8: 禁用2 - 不可交互、存在感弱
 *
 * 功能颜色：
 * P6/P7: 品牌色（紫色）
 * S6/S7: 成功（绿色）
 * E6/E7: 失败/错误（红色）
 * W6/W7: 警示/警告（橙色）
 * B6: 超链接（使用品牌色）
 */
.x-text--color-t1 {
  color: var(--x-text-1); /* 一级/正文 - 标题/强调/选中态 */
}

.x-text--color-t3 {
  color: var(--x-text-3); /* 二级/正文 - 正文/常态 */
}

.x-text--color-t5 {
  color: var(--x-text-5); /* 三级/辅助 - 次要信息 */
}

.x-text--color-t6 {
  color: var(--x-text-6); /* 四级/辅助 - 提示信息 */
}

.x-text--color-t7 {
  color: var(--x-text-7); /* 禁用1 - 提示信息 */
}

.x-text--color-t8 {
  color: var(--x-text-8); /* 禁用2 - 禁用信息 */
}

.x-text--color-p6 {
  color: var(--x-purple-6); /* 品牌色 */
}

.x-text--color-p7 {
  color: var(--x-purple-7); /* 品牌色 - hover/点击 */
}

.x-text--color-s6 {
  color: var(--x-success-6); /* 成功 - 正常icon颜色 */
}

.x-text--color-s7 {
  color: var(--x-success-7); /* 成功 - 主要文字/按钮 */
}

.x-text--color-e6 {
  color: var(--x-error-6); /* 错误 - 正常icon颜色 */
}

.x-text--color-e7 {
  color: var(--x-error-7); /* 错误 - 主要文字/按钮 */
}

.x-text--color-w6 {
  color: var(--x-warning-6); /* 警示 - 正常icon颜色 */
}

.x-text--color-w7 {
  color: var(--x-warning-7); /* 警示 - 主要文字/按钮 */
}

.x-text--color-b6 {
  color: var(--x-purple-6); /* 超链接 - 使用品牌色 */
}

/* Hover 颜色效果 - 统一使用 CSS 变量控制 */
.x-text--has-hover {
  transition: color 0.2s ease;
}

.x-text--has-hover:hover {
  color: var(--x-text-hover-color) !important;
}
</style>
